import * as Constants from "@/shared/constants";
import {WorkScheduleWeekComponent} from "./workScheduleWeekComponent";
import {formatDuration} from "@/shared/utils/datefunctions";
import {TargetWorkingTimeType} from "@/shared/timrTypes";
import {WorkScheduleModelWeek} from "@/shared/timr";

export class WorkScheduleModelWeekComponent extends HTMLElement {

	public static readonly EVENT_UPDATED = "workScheduleModelWeek.updated";
	public static readonly EVENT_CHANGED = "workScheduleModelWeek.changed";
	public static readonly EVENT_REMOVED = "workScheduleModelWeek.removed";

	private readonly template = `
		<div class="flex-row flex-vertical-center">
			<div class="flex-column">
				<div class="form-group">
					<!-- Week label input -->
					<input type="text" class="form-control" data-week-name />
				</div>
			</div>
			<!-- Daily hours and total -->
			<div class="flex-column-auto flex-vertical-center" data-toggle-details>
				<!-- Daily hours display -->
				<div class="flex-row justify-content-end" data-hide-if-details>
					${Constants.WEEKDAYS.map((day) => {
		return `
							<div class="flex-column display-flex-items-center mr-20">
								<div class="text-grey">${messages[`workScheduleModel.${day.toLowerCase()}.short`]}</div>
								<div class="text-bold" data-duration-day="${day}"></div>
							</div>
						`;
	}).join("")}
				</div>
			</div>
				
			<!-- Total summary -->
			<div class="flex-column" data-toggle-details>
				<div class="flex-row flex-vertical-center text-nowrap">
					<span class="text-grey">${messages["workScheduleModel.sum"]}</span>
					<span class="text-bold position-right" data-duration-total></span>
					<div class="pl-20">
						<i class="fas fa-caret-down" data-caret></i>
					</div>
				</div>
			</div>
		</div>
		<div data-details>
			<div class="display-grid">
				<work-schedule-week></work-schedule-week>
				<div class="display-grid justify-content-end">
					<div class="btn btn-icon btn-outline-danger" data-remove-button>
						<i class="far fa-hyphen position-left"></i> ${messages["workScheduleModel.alternating.removeWeek"]}
					</div>
				</div>
			</div>
		</div>
	`;

	private workScheduleModelWeek: WorkScheduleModelWeek | null = null;
	private workScheduleWeekComponent: WorkScheduleWeekComponent;
	private showRemoveButton: boolean = false;
	private weekNumber: number;
	private weekName: string;

	public setWeekNumber(weekNumber: number): void {
		this.weekNumber = weekNumber;
		this.workScheduleWeekComponent.namePrefix = `workScheduleModelWeeks[${weekNumber}]`;
		this.setName();
	}

	public setRemovable(removable: boolean): void {
		this.showRemoveButton = removable;
	}

	public setTargetWorkingTimeType(type: TargetWorkingTimeType): void {
		this.render();
		this.getWorkScheduleWeekComponent().targetWorkingTimeType = type;
	}

	public setWorkScheduleModelWeek(week: WorkScheduleModelWeek): void {
		this.workScheduleModelWeek = week;
		this.weekName = week.name;
		this.render();
		this.getWorkScheduleWeekComponent().workScheduleWeek = week.workScheduleWeek;
	}

	/**
	 * Returns the current work schedule week
	 */
	public getWorkScheduleModelWeek(): WorkScheduleModelWeek | null {
		return {
			workScheduleWeek: this.getWorkScheduleWeekComponent().workScheduleWeek,
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
			name: this.weekName || (this.querySelector("[data-week-name]") as HTMLInputElement)?.value,
			weekNumber: this.weekNumber,
			id: this.workScheduleModelWeek?.id ?? null
		};
	}

	/**
	 * Returns the total work duration in minutes or null if not set
	 */
	public getTotalWorkDuration(): number | null {
		return this.getWorkScheduleWeekComponent().totalWorkDuration;
	}

	public expand() {
		this.toggleDetails(true);
	}

	private render(): void {
		if (!this.innerHTML) {
			this.innerHTML = this.template;
			this.setName();
			this.initListeners();
		}
	}

	private setName() {
		const weekNameInput: HTMLInputElement = this.querySelector("[data-week-name]");
		if (weekNameInput) {
			const placeholder = messages.withArguments("workScheduleModel.alternating.weekNumber", this.weekNumber + 1);
			weekNameInput.name = `workScheduleModelWeeks[${this.weekNumber}].name`;
			weekNameInput.value = this.weekName ?? placeholder;
			weekNameInput.placeholder = placeholder;
		}
	}

	private initListeners(): void {
		// init listeners
		this.getWorkScheduleWeekComponent().addEventListener(WorkScheduleWeekComponent.EVENT_CHANGED, () => {
			this.dispatchEvent(new Event(WorkScheduleModelWeekComponent.EVENT_CHANGED));
		});
		this.getWorkScheduleWeekComponent().addEventListener(WorkScheduleWeekComponent.EVENT_UPDATED, () => {
			this.update();
			this.dispatchEvent(new Event(WorkScheduleModelWeekComponent.EVENT_UPDATED));
		});
		this.querySelectorAll("[data-toggle-details]").forEach((el) => {
			el.addEventListener("click", () => {
				this.toggleDetails();
			});
		});

		this.querySelector("[data-week-name]")?.addEventListener("change", (e) => {
			this.weekName = (e.target as HTMLInputElement)?.value;
		});

		this.querySelector("[data-remove-button]")?.addEventListener("click", () => {
			this.dispatchEvent(new Event(WorkScheduleModelWeekComponent.EVENT_REMOVED));
		});
	}

	private update() {
		Constants.WEEKDAYS.forEach((day) => {
			const dayElement = this.querySelector(`[data-duration-day="${day}"]`);
			if (dayElement) {
				const workDuration = this.getWorkScheduleModelWeek().workScheduleWeek[`workDuration${day}`];
				dayElement.textContent = workDuration ? formatDuration(workDuration) : "-";
			}
		});

		const totalElement = this.querySelector("[data-duration-total]");
		if (totalElement) {
			totalElement.textContent = formatDuration(this.getTotalWorkDuration());
		}

		const removeButtonDiv: HTMLElement = this.querySelector("[data-remove-button]");
		if (removeButtonDiv) {
			removeButtonDiv.style.display = this.showRemoveButton ? "" : "none";
		}

		this.dispatchEvent(new Event(WorkScheduleModelWeekComponent.EVENT_UPDATED));
	}

	private getWorkScheduleWeekComponent(): WorkScheduleWeekComponent {
		if (!this.workScheduleWeekComponent) {
			this.workScheduleWeekComponent = this.querySelector("work-schedule-week");
		}
		return this.workScheduleWeekComponent;
	}

	/**
	 * Toggles the details view
	 * @param force If included, turns the toggle into a one way-only operation.
	 * 				If set to `false`, then details will collapse.
	 * 				If set to `true`, then details will expand.
	 * @private
	 */
	private toggleDetails(force?: boolean): void {
		const details = this.querySelector("[data-details]");
		const caret = this.querySelector("[data-caret]");
		if (details && caret) {
			details.classList.toggle("open", force);
			caret.classList.toggle("open", force);
			this.querySelectorAll("[data-hide-if-details]").forEach((e) => e.classList.toggle("open", force));
		}
	}
}
